import React, { useRef, useMemo, useState, useCallback } from 'react'
import { getButtonPositionClass, getButtonColClass, formatPhoneNumber, buildUploadHint } from './element-options'
import { RhyBusCustomSelect, RhyBusInputText, RhyBusTextArea, RhyBusDatePicker, RhyBusInputCheck, RhyBusLabel, RhyBusSwitch } from '@rhybus/form-controls'
import { RhyBusFileUpload } from '@rhybus/file-upload'
import { RhyBusButton } from '@rhybus/button'
import { RhybusPeoplePicker } from '@rhybus/people-picker'
import { useMetadataOptionsMap } from '../services/metadataManagement'

interface DFRProps {
  schema: { containersData: any[]; selectedLayout: Record<string, number[]> }
  values: Record<string, any>
  handleChange: (key: string, val: any) => void
  formRef: React.MutableRefObject<Record<string, any>>
  formHydrationVersion?: any
  optionsMap?: Record<string, any[]>
  customHandleChange?: (field: any, value: any) => void
}

const NON_INPUT_TYPES = ['SectionHeader', 'SubSectionHeader', 'ContractDurationHeader', 'Button', 'uploadfile']

const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/


const DynamicFormRenderer: React.FC<DFRProps> = ({ schema, values, handleChange, formRef, formHydrationVersion, optionsMap: externalOptionsMap, customHandleChange }) => {
  const fileMapRef = useRef<Record<string, File[]>>({})
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({})

  const validateEmailOnBlur = useCallback((id: string, value: string) => {
    const err = value && !EMAIL_REGEX.test(value) ? 'Please enter a valid email address' : ''
    setFieldErrors(prev => {
      if (!err) { const next = { ...prev }; delete next[id]; return next }
      return { ...prev, [id]: err }
    })
  }, [])

  const validateNumberRange = useCallback((id: string, value: string, min?: number, max?: number) => {
    const num = parseFloat(value)
    let err = ''
    if (value !== '' && !isNaN(num)) {
      if (min != null && num < min) err = `Value must be at least ${min}`
      else if (max != null && num > max) err = `Value must be at most ${max}`
    }
    setFieldErrors(prev => {
      if (!err) { const next = { ...prev }; delete next[id]; return next }
      return { ...prev, [id]: err }
    })
  }, [])

  // Collect all Select fields that have a metaDataType binding so we can resolve
  // their options from metadata at runtime (no hardcoded options in the schema).
  const metadataSelectPairs = useMemo(() =>
    schema.containersData.flatMap(c =>
      c.rowData.flatMap((r: any) =>
        (r.childData as any[])
          .filter(ch => ch.type === 'Select' && ch.metaDataType)
          .map(ch => ({ id: ch.id as string, metaDataType: ch.metaDataType as string }))
      )
    ),
    [schema]
  )
  const metadataOptionsMap = useMetadataOptionsMap(metadataSelectPairs)
  const optionsMap = { ...externalOptionsMap, ...metadataOptionsMap }

  const captureFiles = (key: string, e: React.SyntheticEvent) => {
    const t = e.target as HTMLInputElement
    if (t?.type === 'file' && t.files) fileMapRef.current[key] = Array.from(t.files)
  }

  const onFileChange = (key: string, stripped: any[]) => {
    const actual = fileMapRef.current[key] ?? []
    const merged = stripped.map((s: any) => {
      if (!s || s instanceof File) return s
      return actual.find((f: File) => f.name === (s.name || s.FileName) && f.size === s.size) ?? s
    })
    fileMapRef.current[key] = []
    // Skip update when no valid files were added and the list is already empty.
    // Without this, onChange([]) re-renders the component with files=[], which
    // triggers RhyBusFileUpload's internal useEffect to clear its fileErrors state.
    const current: any[] = values[key] ?? []
    if (merged.length === 0 && current.length === 0) return
    handleChange(key, merged)
  }

  const reg = (key: string, el: any) => {
    const prev = formRef.current[key]
    formRef.current[key] = { ...(typeof prev === 'object' && prev ? prev : {}), el }
  }

  const renderField = (ch: any, colClass: string) => {
    const id: string = ch.internalName || ch.id || ''
    const label: string = ch.label || ''
    const isMandatory: boolean = ch.isMandatory ?? false
    const placeholder: string = ch.placeholder || `Enter ${label}`
    const readOnly: boolean = ch.readonly ?? false
    const disabled: boolean = ch.disabled ?? false
    const tooltipPlacement = (ch.position || 'top') as 'top' | 'bottom' | 'left' | 'right'
    const info: string | undefined = ch.isInfo ? label : undefined

    switch (ch.type) {
      case 'Input':
      case 'text':
      case 'email':
      case 'number':
      case 'phone':
      case 'search':
      case 'currency':
        if (ch.className === 'content-block-item') {
          return (
            <div className={colClass} key={id}>
              <div
                className="content-block-item"
                style={{
                  backgroundColor: ch.bgColor || undefined,
                  color: ch.cbFontColor || undefined,
                  border: ch.cbBorder || undefined,
                  fontSize: ch.cbFontSize || undefined,
                  fontWeight: ch.cbFontWeight || undefined,
                  padding: ch.cbPadding || undefined,
                  borderRadius: ch.cbBorderRadius || undefined,
                }}
              >
                {ch.value || ''}
              </div>
            </div>
          )
        }
        {
          const effectiveIconRight = ch.iconRight ?? (ch.type === 'search')
          const hasLeft = !!ch.iconLeft
          const hasRight = !!effectiveIconRight
          const hasIcons = hasLeft || hasRight
          const iconLeftClass = ch.customIconLeft || ''
          const iconRightClass = ch.customIconRight || (ch.type === 'search' ? 'icon-rb-search' : '')
          const sizeStr = ch.inputSize || 'md'
          const iconPadClass = [hasLeft ? 'icon-left-gap' : '', hasRight ? 'icon-right-gap' : ''].filter(Boolean).join(' ')
          const iconStyle: React.CSSProperties = { position: 'absolute', bottom: 0, width: 36, height: 'var(--rb-form-height, 38px)', display: 'flex', alignItems: 'center', justifyContent: 'center', pointerEvents: 'none', color: 'var(--rb-form-border-color-focus, #273CC2)', fontSize: 14 }
          const inputEl = (
            <RhyBusInputText
              key={readOnly ? `${id}-${formHydrationVersion}-${values[id] ?? ''}` : `${id}-${formHydrationVersion}`}
              id={id}
              label={label}
              type={ch.type === 'Input' ? 'text' : ch.type === 'phone' ? 'tel' : ch.type === 'currency' ? 'currency' : ch.type}
              placeholder={placeholder}
              value={values[id] || ''}
              onChange={(e: any) => {
                let val = e?.target?.value ?? e
                if (ch.type === 'phone') val = formatPhoneNumber(val)
                handleChange(id, val)
                if (ch.type === 'email' && fieldErrors[id]) validateEmailOnBlur(id, val)
                if ((ch.type === 'number' || ch.type === 'currency') && (ch.min != null || ch.max != null)) validateNumberRange(id, val, ch.min, ch.max)
              }}
              onBlur={(e: any) => {
                const val = e?.target?.value ?? ''
                if (ch.type === 'email') validateEmailOnBlur(id, val)
                if ((ch.type === 'number' || ch.type === 'currency') && (ch.min != null || ch.max != null)) validateNumberRange(id, val, ch.min, ch.max)
              }}
              ref={(el: any) => reg(id, el)}
              labelClassName=""
              isMandatory={isMandatory}
              info={info}
              tooltipPlacement={tooltipPlacement}
              readOnly={readOnly}
              disabled={disabled}
              error={!!fieldErrors[id]}
              errorMessage={fieldErrors[id]}
              {...(ch.inputSize ? { size: ch.inputSize as any } : {})}
              {...(ch.formlayout ? { formlayout: ch.formlayout as any } : {})}
              {...(ch.maxLength != null ? { maxLength: ch.maxLength } : ch.type === 'phone' ? { maxLength: 17 } : {})}
              {...(ch.min != null ? { min: ch.min } : {})}
              {...(ch.max != null ? { max: ch.max } : {})}
              {...(ch.type === 'currency' && ch.currency ? { currency: ch.currency } : {})}
              {...(ch.type === 'currency' && ch.decimalPlaces != null ? { decimalPlaces: ch.decimalPlaces } : {})}
              wrapperClassName={ch.type === 'search' ? 'rb-search-input' : ''}
              iconLeft={false}
              iconRight={false}
              className={iconPadClass}
              {...(ch.maxCount != null ? { maxCount: ch.maxCount } : {})}
              {...(ch.inline ? { inline: ch.inline } : {})}
              {...(ch.inlineType ? { inlineType: ch.inlineType as any } : {})}
              {...(ch.isBadge ? { isBadge: ch.isBadge, count: String(values[id] ?? '').length } : {})}
              {...(ch.autoComplete ? { autoComplete: ch.autoComplete } : {})}
            />
          )
          return (
            <div className={colClass} key={id}>
              {hasIcons ? (
                <div className={`rb-form-size-${sizeStr}`} style={{ position: 'relative' }}>
                  {inputEl}
                  {hasLeft && iconLeftClass && <span aria-hidden style={{ ...iconStyle, left: 0 }}><span className={iconLeftClass} /></span>}
                  {hasRight && iconRightClass && <span aria-hidden style={{ ...iconStyle, right: 0 }}><span className={iconRightClass} /></span>}
                </div>
              ) : inputEl}
            </div>
          )
        }

      case 'Select':
        return (
          // data-rb-select on the real DOM wrapper so focusField can find it with
          // [data-rb-select="${id}"] input — the react-select hidden input lives inside here.
          <div
            className={colClass}
            key={id}
            data-rb-select={id}
            ref={(el: HTMLDivElement | null) => { if (el) reg(id, el) }}
          >
            <RhyBusCustomSelect
              id={id}
              label={label}
              placeholder={placeholder}
              options={optionsMap[id] ?? ch.options ?? []}
              value={values[id] || null}
              onChange={(v: any) => ch.onChange ? ch.onChange(v) : customHandleChange ? customHandleChange(ch, v) : handleChange(id, v)}
              labelClassName="inter-medium font-14"
              isMandatory={isMandatory}
              info={info}
              tooltipPlacement={tooltipPlacement}
              isDisabled={disabled}
              {...(ch.isMulti ? { isMulti: true } : {})}
              {...(ch.inputSize ? { size: ch.inputSize } : {})}
              {...(ch.formlayout ? { formlayout: ch.formlayout } : {})}
              {...(ch.isBadge ? { isBadge: ch.isBadge, count: Array.isArray(values[id]) ? values[id].length : (values[id] != null ? 1 : 0) } : {})}
              {...(ch.maxCount != null ? { maxCount: ch.maxCount } : {})}
              {...(ch.inline ? { inline: ch.inline } : {})}
              {...(ch.inlineType ? { inlineType: ch.inlineType as any } : {})}
            />
          </div>
        )

      case 'Textarea':
        return (
          <div className={colClass} key={id}>
            <RhyBusTextArea
              id={id}
              label={label}
              placeholder={placeholder}
              value={values[id] || ''}
              onChange={(e: any) => handleChange(id, e?.target?.value ?? e)}
              rows={ch.rows ?? 5}
              ref={(el: any) => reg(id, el)}
              labelClassName=""
              isMandatory={isMandatory}
              info={info}
              tooltipPlacement={tooltipPlacement}
              disabled={disabled}
              readOnly={readOnly}
              {...(ch.inputSize ? { size: ch.inputSize as any } : {})}
              {...(ch.formlayout ? { formlayout: ch.formlayout as any } : {})}
              {...(ch.maxLength != null ? { maxLength: ch.maxLength } : {})}
              {...(ch.isBadge ? { isBadge: ch.isBadge, count: String(values[id] ?? '').length } : {})}
              {...(ch.maxCount != null ? { maxCount: ch.maxCount } : {})}
              {...(ch.inline ? { inline: ch.inline } : {})}
              {...(ch.inlineType ? { inlineType: ch.inlineType as any } : {})}
            />
          </div>
        )

      case 'DatePicker':
        return (
          <div className={colClass} key={id} ref={(el: any) => reg(id, el)}>
            <RhyBusDatePicker
              id={id}
              label={label}
              value={values[id] || null}
              onChange={(v: any) => handleChange(id, v)}
              placeholder={placeholder}
              formlayout={ch.formlayout || 'vertical'}
              isMandatory={isMandatory}
              labelClassName=""
              onOpenChange={() => {}}
              disabled={disabled}
              {...(ch.mode ? { mode: ch.mode as any } : {})}
              {...(ch.minDate ? { minDate: new Date(ch.minDate) } : {})}
              {...(ch.maxDate ? { maxDate: new Date(ch.maxDate) } : {})}
              {...(info ? { info, tooltipPlacement } : {})}
              {...(ch.isBadge ? { isBadge: ch.isBadge, count: values[id] != null ? 1 : 0 } : {})}
              {...(ch.maxCount != null ? { maxCount: ch.maxCount } : {})}
              {...(ch.inline ? { inline: ch.inline } : {})}
              {...(ch.inlineType ? { inlineType: ch.inlineType as any } : {})}
            />
          </div>
        )

      case 'uploadfile':
        return (
          <div className={colClass} key={id}>
            <div onChangeCapture={(e: any) => captureFiles(id, e)}>
              <RhyBusFileUpload
                label={label || 'Attachments'}
                files={values[id] || []}
                onChange={(files: any) => onFileChange(id, files)}
                mandatory={isMandatory}
                disabled={disabled}
                labelClassName=""
                {...(info ? { info, tooltipPlacement } : {})}
                hint={ch.hint || buildUploadHint(ch.acceptedTypes, ch.maxFileSizeMB, ch.maxFiles)}
                note={ch.note || 'Screenshots need to be saved locally before uploading into system.'}
                {...(ch.chooseFileLabel ? { chooseFileLabel: ch.chooseFileLabel } : {})}
                {...(ch.maxFiles != null ? { maxFiles: ch.maxFiles } : {})}
                {...(ch.maxFileSizeMB != null ? { maxFileSize: ch.maxFileSizeMB } : {})}
                {...(ch.acceptedTypes ? { acceptedTypes: ch.acceptedTypes.split(',').map((s: string) => s.trim()).filter(Boolean) } : {})}
                {...(ch.removeMessage ? { removeMessage: ch.removeMessage } : {})}
                {...(ch.replaceMessage ? { replaceMesage: ch.replaceMessage } : {})}
                {...(ch.isBadge ? { isBadge: ch.isBadge, count: Array.isArray(values[id]) ? values[id].length : 0 } : {})}
                {...(ch.maxCount != null ? { maxCount: ch.maxCount } : {})}
                {...(ch.inline ? { inline: ch.inline } : {})}
                {...(ch.inlineType ? { inlineType: ch.inlineType as any } : {})}
              />
            </div>
          </div>
        )

      case 'PeoplePicker':
        return (
          <div className={`${colClass} ${getButtonColClass(ch.checkPosition)}`} key={id} ref={(el: any) => reg(id, el)}>
            <div className={getButtonPositionClass(ch.checkPosition) || ''}>
              <div className="form-vertical">
                <RhyBusLabel
                  htmlFor={`peoplepicker-${id}`}
                  label={label}
                  isMandatory={isMandatory}
                  labelClassName=""
                  {...(info ? { info, tooltipPlacement } : {})}
                />
                <RhybusPeoplePicker
                  id={`peoplepicker-${id}`}
                  peoplePickerData={[]}
                  value={values[id] || null}
                  onSelect={(person: any) => handleChange(id, person)}
                  onChange={(val: string) => { if (!val) handleChange(id, null) }}
                  {...(ch.displayText ? { displayText: ch.displayText } : {})}
                />
              </div>
            </div>
          </div>
        )

      case 'SectionHeader':
        return (
          <div className={colClass} key={id}>
            <div className="w-100" style={{ backgroundColor: '#EEF0FF', borderRadius: '6px', padding: '8px 12px' }}>{label}</div>
          </div>
        )

      case 'SubSectionHeader':
        return (
          <div className={colClass} key={id}>
            <div className="w-100" style={{ backgroundColor: '#EFF7FF', color: '#004993', borderRadius: '6px', padding: '8px 12px' }}>{label}</div>
          </div>
        )

      case 'Button':
        return (
          <div className={`${colClass} ${getButtonColClass(ch.buttonPosition)}`} key={id}>
            <div className={`${getButtonPositionClass(ch.buttonPosition) || (ch.subChild?.length > 0 ? 'd-flex' : '')} ${ch.subChild?.length > 0 ? 'align-items-center gap-2 flex-wrap' : ''}`.trim()}>
              <RhyBusButton
                label={label}
                disabled={ch.disabled ?? false}
                {...(ch.variant ? { variant: ch.variant as any } : {})}
                {...(ch.color ? { color: ch.color as any } : {})}
                {...(ch.size ? { size: ch.size as any } : {})}
                {...(ch.buttonType ? { type: ch.buttonType as any } : {})}
                {...(ch.pill ? { pill: ch.pill } : {})}
                leftIcon={ch.leftIcon || ''}
                rightIcon={ch.rightIcon || ''}
                buttonClassName={ch.buttonClassName || ''}
              />
              {ch.subChild?.map((sub: any) => (
                <RhyBusButton
                  key={sub.id}
                  label={sub.label || ''}
                  disabled={sub.disabled ?? false}
                  {...(sub.variant ? { variant: sub.variant as any } : {})}
                  {...(sub.color ? { color: sub.color as any } : {})}
                  {...(sub.size ? { size: sub.size as any } : {})}
                  {...(sub.buttonType ? { type: sub.buttonType as any } : {})}
                  {...(sub.pill ? { pill: sub.pill } : {})}
                  leftIcon={sub.leftIcon || ''}
                  rightIcon={sub.rightIcon || ''}
                  buttonClassName={sub.buttonClassName || ''}
                />
              ))}
            </div>
          </div>
        )

      case 'Radio':
      case 'radio': {
        const radioOpts: string[] = ch.isGroup && ch.options?.length ? ch.options.map((o: any) => o.label) : ['Yes', 'No']
        const radioGroup = ch.isGroup && ch.options?.length
        return (
          <div className={`${colClass} ${getButtonColClass(ch.checkPosition)}`} key={id}>
            <div className={getButtonPositionClass(ch.checkPosition) || ''}>
              {radioGroup ? (
                <fieldset className="rb-option-group">
                  <legend className="">
                    {label}{isMandatory && <span className="rb-label-mandatory" aria-hidden="true"> *</span>}
                    {info && <RhyBusLabel htmlFor={`radio-${id}`} label="" info={info} tooltipPlacement={tooltipPlacement} />}
                  </legend>
                  <div className="d-flex gap-3 flex-wrap">
                    {radioOpts.map(opt => (
                      <RhyBusInputCheck
                        key={`${id}-${formHydrationVersion}-${opt}`}
                        id={`radio-${id}-${opt.toLowerCase().replace(/\s+/g, '-')}`}
                        name={id}
                        type="radio"
                        label={opt}
                        checked={values[id] === opt}
                        onChange={() => handleChange(id, opt)}
                        disabled={disabled || readOnly}
                      />
                    ))}
                  </div>
                </fieldset>
              ) : (
                <div className="form-vertical">
                  <RhyBusLabel
                    htmlFor={`radio-${id}`}
                    label={label}
                    isMandatory={isMandatory}
                    {...(info ? { info, tooltipPlacement } : {})}
                  />
                  <div className="d-flex gap-3 flex-wrap">
                    {radioOpts.map(opt => (
                      <RhyBusInputCheck
                        key={`${id}-${formHydrationVersion}-${opt}`}
                        id={`radio-${id}-${opt.toLowerCase().replace(/\s+/g, '-')}`}
                        name={id}
                        type="radio"
                        label={opt}
                        checked={values[id] === opt}
                        onChange={() => handleChange(id, opt)}
                        disabled={disabled || readOnly}
                      />
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>
        )
      }

      case 'checkbox': {
        if (ch.isGroup && ch.options?.length) {
          return (
            <div className={`${colClass} ${getButtonColClass(ch.checkPosition)}`} key={id}>
              <div className={getButtonPositionClass(ch.checkPosition) || ''}>
                <fieldset className="rb-option-group">
                  <legend className="">
                    {label}{isMandatory && <span className="rb-label-mandatory" aria-hidden="true"> *</span>}
                    {info && <RhyBusLabel htmlFor={`checkbox-group-${id}`} label="" info={info} tooltipPlacement={tooltipPlacement} />}
                  </legend>
                  <div className="d-flex gap-3 flex-wrap">
                    {ch.options.map((opt: any) => {
                      const optVal: string = opt.label
                      const checked = Array.isArray(values[id]) ? values[id].includes(optVal) : false
                      return (
                        <RhyBusInputCheck
                          key={`${id}-${formHydrationVersion}-${optVal}`}
                          id={`checkbox-${id}-${optVal.toLowerCase().replace(/\s+/g, '-')}`}
                          name={id}
                          type="checkbox"
                          label={optVal}
                          checked={checked}
                          onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                            const current = Array.isArray(values[id]) ? [...values[id]] : []
                            handleChange(id, e.target.checked ? [...current, optVal] : current.filter(v => v !== optVal))
                          }}
                          disabled={disabled || readOnly}
                          labelClassName=""
                        />
                      )
                    })}
                  </div>
                </fieldset>
              </div>
            </div>
          )
        }
        return (
          <div className={`${colClass} ${getButtonColClass(ch.checkPosition)}`} key={id}>
            <div className={getButtonPositionClass(ch.checkPosition) || ''}>
              <RhyBusInputCheck
                key={`${id}-${formHydrationVersion}`}
                id={id}
                name={id}
                type="checkbox"
                label={label}
                checked={!!values[id]}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) => handleChange(id, e.target.checked)}
                isMandatory={isMandatory}
                info={info}
                tooltipPlacement={tooltipPlacement}
                disabled={disabled || readOnly}
                labelClassName=""
              />
            </div>
          </div>
        )
      }

      case 'Switch':
        return (
          <div className={`${colClass} ${getButtonColClass(ch.checkPosition)}`} key={id}>
            <div className={getButtonPositionClass(ch.checkPosition) || ''}>
              <div className="form-vertical">
                <RhyBusLabel
                  htmlFor={id}
                  label={label}
                  isMandatory={isMandatory}
                  labelClassName=""
                  {...(info ? { info, tooltipPlacement } : {})}
                />
                <RhyBusSwitch
                  id={id}
                  checked={!!values[id]}
                  onChange={(v: boolean) => handleChange(id, v)}
                  disabled={disabled}
                  ref={(el: any) => reg(id, el)}
                  {...(ch.inputSize ? { size: ch.inputSize as any } : {})}
                  {...(ch.firstLabel ? { firstLabel: ch.firstLabel } : {})}
                  {...(ch.secondLabel ? { secondLabel: ch.secondLabel } : {})}
                  {...(info ? { info, tooltipPlacement } : {})}
                  {...(ch.isBadge ? { isBadge: ch.isBadge, count: values[id] ? 1 : 0 } : {})}
                  {...(ch.maxCount != null ? { maxCount: ch.maxCount } : {})}
                  {...(ch.inline ? { inline: ch.inline } : {})}
                  {...(ch.inlineType ? { inlineType: ch.inlineType as any } : {})}
                />
              </div>
            </div>
          </div>
        )

      default:
        return <div className={colClass} key={id} />
    }
  }

  return (
    <>
      {schema.containersData.map((container: any) => (
        <div key={container.id} id={container.id} className="row g-2 form-md darktext">
          {container.rowData.map((row: any) => {
            const rowLayout = schema.selectedLayout[row.id]
            return (
              <div key={row.id} id={row.id} className="row p-relative g-2 form-md darktext">
                {row.childData.map((ch: any, idx: number) => {
                  const colNum = rowLayout ? rowLayout[idx % rowLayout.length] : 12
                  const colClass = colNum === 20 ? `col-12 col-md-6 col-lg-${colNum}per` : `col-12 col-md-6 col-lg-${colNum}`
                  return renderField(ch, colClass)
                })}
              </div>
            )
          })}
        </div>
      ))}
    </>
  )
}

export { NON_INPUT_TYPES }
export default DynamicFormRenderer
